/**
 * Demo data provider — a verbatim TypeScript port of the prototype's `slice-data.js`.
 *
 * The field is generated from a seeded RNG (`mulberry32(20260722)`) and the **call order of
 * `rnd()` is load-bearing**: it is what makes this field byte-for-byte the one in the
 * prototype. Do not reorder, add or remove `rnd()` calls, and keep the `&&` short-circuits
 * exactly as they are — they decide whether a draw happens at all.
 *
 * Site totals are anchored to the UKDC deck; per-SKU points are simulated for demonstration.
 * Screens must not import this file — go through `hooks.ts` / `index.ts`.
 */

import { type AgentAnchors, capacityAnswer, fieldAnswer } from "./agent";
import { CAP_YEARS, RELO_STOPS, UNIT_SKU } from "./math";
import type { DataProvider } from "./provider";
import { MONTHS } from "./served";
import type {
  AgentContext,
  Band,
  CapacityAnchors,
  CapacityModel,
  Channel,
  DataLabels,
  GeoMarket,
  LensKind,
  Lever,
  LeverFamily,
  LeverFamilySample,
  LeverId,
  NpiFamily,
  PickLocation,
  ReportId,
  ReportMeta,
  ReportRow,
  Site,
  Sku,
  SkuType,
  SpaceLeverId,
} from "./types";

/* ============ seeded RNG ============ */

function mulberry32(seed: number): () => number {
  let a = seed;
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const rnd = mulberry32(20260722);
const R = (a: number, b: number) => a + (b - a) * rnd();
const pick = <T>(arr: readonly T[]): T => arr[Math.floor(rnd() * arr.length)] as T;

/* ============ reference tables ============ */

/** brand: name, type, box price (£/box) */
const BRANDS: readonly [string, SkuType, number][] = [
  ["Oasys for Astigmatism", "Rx", 34],
  ["Oasys HydraLuxe Astig", "Rx", 38],
  ["1-Day Oasys Max Astig", "Rx", 42],
  ["1-Day Moist for Astig", "Rx", 30],
  ["1-Day Max Multifocal", "Rx", 46],
  ["Oasys with HydraLuxe", "Rx", 30],
  ["1-Day Moist MF Lacreon", "Rx", 44],
  ["Oasys Multifocal", "Rx", 40],
  ["1-Day Moist", "Rx", 22],
  ["1-Day Oasys Max", "Rx", 34],
  ["Acuvue Oasys", "Rx", 26],
  ["Acuvue Vita", "Rx", 36],
  ["1-Day TruEye", "Rx", 24],
  ["Acuvue 2", "Rx", 16],
  ["Vita for Astigmatism", "Rx", 50],
  ["Define fitting set", "Dx", 0],
  ["Oasys Astig fitting", "Dx", 0],
  ["Max MF trial", "Dx", 0],
  ["TruEye trial", "Dx", 0],
];

const SIZES = ["30P", "90P", "6P", "5P", "3P", "12P", "1P", "10P"] as const;

export const CHANNELS: readonly Channel[] = [
  { id: "ret", label: "Retailers", color: "#9E0000" },
  { id: "dtp", label: "Direct-to-Patient", color: "#EB1700" },
  { id: "dist", label: "Distributors", color: "#C1BBB3" },
  { id: "ecom", label: "E-commerce", color: "#81766F" },
  { id: "hosp", label: "Hospitals & clinics", color: "#D5CFC9" },
];

/** EMEA account geography. */
export const GEO_MARKETS: readonly GeoMarket[] = [
  { name: "United Kingdom", lon: -2, lat: 54, weight: 26 },
  { name: "Ireland", lon: -8, lat: 53.2, weight: 6 },
  { name: "Germany", lon: 10.4, lat: 51, weight: 18 },
  { name: "France", lon: 2.3, lat: 47, weight: 13 },
  { name: "Iberia", lon: -4, lat: 40, weight: 10 },
  { name: "Italy", lon: 12.5, lat: 43, weight: 9 },
  { name: "Netherlands", lon: 5.3, lat: 52.2, weight: 6 },
  { name: "Belgium", lon: 4.5, lat: 50.8, weight: 5 },
  { name: "Nordics", lon: 15, lat: 60, weight: 8 },
  { name: "Poland", lon: 20, lat: 52, weight: 7 },
  { name: "Czechia", lon: 15.5, lat: 49.8, weight: 4 },
  { name: "Romania", lon: 25, lat: 46, weight: 4 },
  { name: "Baltics", lon: 24, lat: 57, weight: 3 },
  { name: "Greece", lon: 22, lat: 39, weight: 3 },
];

/** Velocity de-rating per review year. */
export const YEAR_FACTORS: Readonly<Record<number, number>> = { 2023: 0.72, 2024: 0.88, 2025: 1 };

const LAUNCH_YEARS = ["’18", "’19", "’20", "’21", "’22", "’23", "’24"] as const;
/** Face-equivalents of depth an A-frame lane needs to stay fed — same rule the build derives. */
const LANE_FACES = 2;

/**
 * Recommended pick location, from velocity band and depth held — the same two measured inputs the
 * real build uses, so both providers answer the slotting question the same way.
 *
 * There is no simulated "current slot" counterpart: it needs a pick-location feed no drop carries,
 * and inventing one made every row of the slotting report a fabrication.
 */
function pickRecoOf(band: Band, faces: number): PickLocation {
  if (band === "A" && faces >= LANE_FACES) {
    return "A-frame";
  }
  return band === "A" || band === "B" ? "OSR automation" : "Small-bin";
}

/* ============ the field ============ */

const SKUS: Sku[] = [];
let id = 0;
for (const [bn, typ, price] of BRANDS) {
  // ~half Dx, reflecting 15.3k Rx / 14.5k Dx
  const n = typ === "Dx" ? 20 + Math.floor(rnd() * 11) : 5 + Math.floor(rnd() * 5);
  for (let i = 0; i < n; i++) {
    const size = pick(SIZES);
    // velocity is the driver
    let vel: number;
    let noShip = false;
    const roll = rnd();
    if (typ === "Dx") {
      vel = R(15, 240); // Dx always ship (the £0 lane) — never non-movers
    } else if (roll < 0.19) {
      noShip = true; // the tail: held but essentially not shipping in the window
      vel = R(0, 6);
    } else if (roll < 0.36) {
      vel = R(160, 850);
    } else {
      vel = Math.min(9600, Math.pow(rnd(), 1.7) * 9600 * R(0.6, 1.15) + R(300, 700));
    }
    // VALUE derived from velocity x price. premium price → high value even at lower velocity
    const value = typ === "Dx" ? 0 : Math.round(vel * 12 * price * R(0.9, 1.1));
    const band: Band = vel > 3200 ? "A" : vel > 850 ? "B" : vel > 200 ? "C" : "D";
    const slow = band === "C" || band === "D";
    // inventory on hand. Rx: rises with velocity + a minority over-held. Dx: fitting-range
    // depth, largely independent of velocity — so Dx spreads across the inventory (right) axis.
    const overstock = slow && rnd() < 0.14;
    let inv: number;
    if (typ === "Dx") {
      inv = Math.round(40 + Math.pow(rnd(), 1.7) * 2600);
    } else if (overstock) {
      inv = Math.round(R(1900, 4700));
    } else {
      inv = Math.round(Math.min(4300, 200 + Math.sqrt(vel) * R(17, 31) + R(0, 110)));
    }
    const faces = Math.max(1, Math.round(inv / 620));
    // slow but genuinely valuable (top-left)
    const premiumSlow = slow && price >= 44 && value > 250000;
    // recently-launched NPI — velocity is still ramping, so low velocity ≠ dead stock.
    // Protect from rationalisation.
    const isNew = !noShip && !premiumSlow && slow && rnd() < 0.3;
    // problematic: Rx = slow + low value (bottom-left) · Dx = slow + heavily stocked.
    // NPI-new never flagged (still ramping).
    const worth =
      !premiumSlow &&
      !isNew &&
      (typ === "Dx" ? vel < 130 && inv > 1250 : band === "D" || (band === "C" && value < 60000));
    const pickReco = pickRecoOf(band, faces);
    // last shipment recency (quarters ago) — for non-mover reports
    const lastMovedQ = noShip ? 2 + Math.floor(rnd() * 6) : 0;
    let cm = CHANNELS.map(() => R(0.03, 1));
    if (typ === "Dx") {
      cm = [0.1, 0.05, 0.05, 0.03, 0.9];
    }
    const cmt = cm.reduce((a, b) => a + b, 0);
    cm = cm.map((x) => x / cmt);
    const ph = R(0, 6);
    let mon = MONTHS.map(
      (_, m) => 0.55 + 0.45 * Math.sin(((m + ph) / 12) * Math.PI * 2) + R(-0.12, 0.12),
    );
    const mt = mon.reduce((a, b) => a + b, 0);
    mon = mon.map((x) => Math.max(0.03, x) / mt);
    const gseed = mulberry32(id * 97 + 13);
    const geo = GEO_MARKETS.filter(() => gseed() > 0.35);
    const skuId = id++;
    const accounts = Math.max(2, Math.round((vel / 9600) * 760) + Math.round(R(2, 50)));
    const launched = isNew
      ? "Q" + (1 + Math.floor(rnd() * 2)) + " ’25"
      : "Q" + (1 + Math.floor(rnd() * 4)) + " " + pick(LAUNCH_YEARS);
    SKUS.push({
      id: skuId,
      name: bn + " · " + size,
      brand: bn,
      size,
      type: typ,
      price,
      vel: Math.round(vel),
      inv,
      value,
      band,
      worth,
      premiumSlow,
      isNew,
      faces,
      pickReco,
      lastMovedQ,
      accounts,
      launched,
      chan: cm,
      mon,
      geo: geo.length ? [...geo] : [GEO_MARKETS[0] as GeoMarket, GEO_MARKETS[2] as GeoMarket],
    });
  }
}

const SITE: Site = {
  heldSKUs: 29774,
  shippedSKUs: 24601,
  nts: 160330424,
  pickCap: 7500,
  occ: 0.94,
  npiOver: 0.1,
  accounts: 1412,
  deadCount: 29774 - 24601,
};

/* ===== forward capacity ===== */
// The ceiling is a planning INPUT (deck slide 12, "Pick Face Capacity"): no feed states how many
// SKUs the pick faces hold. The markers are shares of it, and the baseline is the SKUs the site is
// counted as holding — so the capacity chart starts from the same portfolio the field plots.
const CAP_CEIL = 33371;
const CAPD: CapacityAnchors = {
  ceil: CAP_CEIL,
  p80: Math.round(CAP_CEIL * 0.8),
  p90: Math.round(CAP_CEIL * 0.9),
  base2026: SITE.heldSKUs,
};

/** NPI product families — release year and the total SKU count each introduces to the DC. */
const NPI_FAM: readonly NpiFamily[] = [
  { id: "n26a", name: "MAX RU S 2W", total: 381, rel: 2026 },
  { id: "n27a", name: "MAX RU A 2W", total: 9900, rel: 2027 },
  { id: "n27b", name: "MAX A 90P", total: 2260, rel: 2027 },
  { id: "n28a", name: "MAX RU MF 2W", total: 366, rel: 2028 },
  { id: "n29a", name: "ORION S", total: 414, rel: 2029 },
];

const CAPACITY: CapacityModel = {
  capd: CAPD,
  years: [...CAP_YEARS],
  npiFamilies: [...NPI_FAM],
  inputs: {
    source: "UKDC capacity planning deck (slide 12), carried into this demo fixture",
    asOf: null,
    ceiling: CAP_CEIL,
    npiTotal: NPI_FAM.reduce((a, f) => a + f.total, 0),
    baselineBasis: `the ${SITE.heldSKUs.toLocaleString()} SKUs this site is counted as holding`,
  },
};

/* ===== SITE-SCALE candidate model =====
 * The scatter above is a representative SAMPLE for legibility; these scenario figures are
 * the real UKDC magnitudes (deck-grounded). A family = brand x representative pack, spanning
 * many power/cylinder/axis SKUs, so one toric or multifocal family can hold thousands.
 */

const KIND_TAG: Record<LensKind, string> = { tor: "toric", mf: "multifocal", sph: "spherical" };

function lensCode(kind: LensKind, g: () => number): string {
  const p = -(1 + Math.floor(g() * 40)) * 0.25;
  const sph = (p <= 0 ? "-" : "+") + Math.abs(p).toFixed(2);
  if (kind === "tor") {
    const cyl = ["-0.75", "-1.25", "-1.75", "-2.25"][Math.floor(g() * 4)] ?? "";
    const ax = (1 + Math.floor(g() * 18)) * 10;
    return sph + " / " + cyl + " x " + ax;
  }
  if (kind === "mf") {
    return sph + " · ADD " + (["LOW", "MED", "HIGH"][Math.floor(g() * 3)] ?? "");
  }
  return sph;
}

function mkFam(
  leverId: SpaceLeverId,
  prefix: string,
  i: number,
  brand: string,
  pack: string,
  base: number,
  kind: LensKind,
): LeverFamily {
  const g = mulberry32(9973 + prefix.charCodeAt(0) * 131 + i * 17 + base);
  const ns = Math.min(14, base);
  const samples: LeverFamilySample[] = [];
  for (let k = 0; k < ns; k++) {
    samples.push({
      code: lensCode(kind, g),
      mv: Math.floor(g() * 100),
      held: 20 + Math.floor(g() * 580),
    });
  }
  const gv = 0.72 + g() * 0.56;
  return {
    id: prefix + i,
    leverId,
    brand,
    pack,
    kind,
    kindTag: KIND_TAG[kind],
    base,
    label: brand + " · " + pack,
    gv,
    samples,
  };
}

type FamSpec = [string, string, number, LensKind];

/** non-mover base counts sum to ~5,173 (deck: held 29,774 − shipped 24,601) */
const RELO_FAM: LeverFamily[] = (
  [
    ["1-Day Max Multifocal", "30-pack", 1830, "mf"],
    ["Oasys for Astigmatism", "90-pack", 683, "tor"],
    ["1-Day Oasys Max Astig", "30-pack", 540, "tor"],
    ["Oasys HydraLuxe Astig", "90-pack", 410, "tor"],
    ["1-Day Moist for Astig", "30-pack", 300, "tor"],
    ["1-Day Moist MF Lacreon", "30-pack", 260, "mf"],
    ["Vita for Astigmatism", "6-pack", 260, "tor"],
    ["Oasys Multifocal", "6-pack", 210, "mf"],
    ["Acuvue Oasys", "12-pack", 150, "sph"],
    ["Oasys with HydraLuxe", "6-pack", 120, "sph"],
    ["1-Day Oasys Max", "30-pack", 110, "sph"],
    ["1-Day Moist", "90-pack", 90, "sph"],
    ["Acuvue Vita", "6-pack", 80, "sph"],
    ["1-Day TruEye", "30-pack", 70, "sph"],
    ["Acuvue 2", "6-pack", 60, "sph"],
  ] satisfies FamSpec[]
).map((d, i) => mkFam("relo", "r", i, d[0], d[1], d[2], d[3]));

/** low-running Dx fitting sets convertible to an Rx sample packet (~2,600) */
const CONV_FAM: LeverFamily[] = (
  [
    ["Define fitting set", "trial set", 900, "sph"],
    ["Oasys Astig fitting", "trial set", 780, "tor"],
    ["Max MF trial", "trial set", 560, "mf"],
    ["TruEye trial", "trial set", 360, "sph"],
  ] satisfies FamSpec[]
).map((d, i) => mkFam("conv", "c", i, d[0], d[1], d[2], d[3]));

/** larger packs rebuildable from a smaller pack the site already holds (~3,400) */
const PACK_FAM: LeverFamily[] = (
  [
    ["1-Day Moist", "90-pack ← 30-pack", 720, "sph"],
    ["1-Day Max Multifocal", "90-pack ← 30-pack", 640, "mf"],
    ["Acuvue Oasys", "12-pack ← 6-pack", 430, "sph"],
    ["1-Day TruEye", "90-pack ← 30-pack", 390, "sph"],
    ["Oasys for Astigmatism", "90-pack ← 30-pack", 360, "tor"],
    ["1-Day Moist", "10-pack ← 5-pack", 300, "sph"],
    ["Acuvue 2", "12-pack ← 6-pack", 300, "sph"],
    ["1-Day Oasys Max", "90-pack ← 30-pack", 260, "sph"],
  ] satisfies FamSpec[]
).map((d, i) => mkFam("pack", "p", i, d[0], d[1], d[2], d[3]));

const FAM: Record<SpaceLeverId, LeverFamily[]> = {
  relo: RELO_FAM,
  conv: CONV_FAM,
  pack: PACK_FAM,
};

const LEVERS: readonly Lever[] = [
  {
    id: "relo",
    title: "SKU Relocation",
    icon: "relo",
    capital: false,
    desc: "Slow movers whose demand a regional hub could serve — move them off-site.",
    tradeoff: "Frees holdings here; longer lead time on those SKUs.",
    reco: "Relocate to regional hub",
    reloStops: [...RELO_STOPS],
  },
  {
    id: "conv",
    title: "Rx → Dx Conversion",
    icon: "conv",
    capital: false,
    desc: "Low-running Dx samples replaced by shipping a small Rx packet — eliminates the duplicate Dx SKU.",
    tradeoff: "Removes duplicate Dx SKUs; also helps EU MDR/UDI compliance.",
    reco: "Convert to Rx-packet",
  },
  {
    id: "pack",
    title: "Pack Rationalisation",
    icon: "pack",
    capital: false,
    desc: "Drop larger packs that can be rebuilt from a smaller pack the site already holds.",
    tradeoff: "Ship 2× the smaller pack when the larger is ordered.",
    reco: "Rebuild from smaller pack",
  },
  {
    id: "cap",
    title: "Increase Capacity",
    icon: "cap",
    capital: true,
    desc: "Raise the ceiling through investment — in whole automation units.",
    tradeoff: `The capital fallback. Each automation unit ≈ ${UNIT_SKU.toLocaleString()} SKU-slots.`,
    reco: "",
  },
];

/* ============ reports ============ */

function fmtMoneyShort(v: number): string {
  const n = Math.round(v);
  return n >= 1e6
    ? "£" + (n / 1e6).toFixed(1) + "m"
    : n >= 1e3
      ? "£" + Math.round(n / 1e3) + "k"
      : "£" + n;
}

/** What the rail and the report headers say about this data (R-005). */
export const DEMO_LABELS: DataLabels = {
  badge: "Demo data",
  caption:
    "Site totals anchored to the UKDC deck. Per-SKU points & widget feeds simulated for demonstration.",
  notes: [],
};

/** Titles, columns and narrative copy for the two report screens. */
export const REPORT_META: Readonly<Record<ReportId, ReportMeta>> = {
  storage: {
    title: "Slotting review",
    sub: "SKUs whose velocity and depth call for a lane or automation",
    cols: ["SKU", "Velocity", "Held", "Depth (est.)", "Recommended", "Why"],
    narrative: (n) =>
      `<b>${n} SKUs</b> move fast enough — and are held deep enough — to belong in an A-frame lane or in automation, fastest first. <b>This is a recommendation, not a mismatch list:</b> no pick-location feed exists in this data, so nothing here claims where a SKU sits today. Check each against the WMS; Accept or Remove, and the accepted set is what you action.`,
  },
  nonmover: {
    title: "Non-mover relocation review",
    sub: "SKUs with no shipment in the review window",
    cols: ["SKU", "Last shipped", "Value", "Recommended", "Why"],
    narrative: (n) =>
      `<b>${n} SKUs</b> in this representative sample haven’t shipped in the last review window yet still hold a pick face — the full UKDC set is <b>~5,170 non-movers</b>. Relocating or de-listing them frees space without touching anything that moves. The accepted set can feed the SKU Relocation capacity scenario.`,
  },
};

/** Why a SKU carries its recommendation — the review's last column. */
function recoWhy(s: Sku): string {
  if (s.pickReco === "A-frame") {
    return `${s.band}-band mover held ≈${s.faces} face-equivalents deep — volume and depth for a dedicated lane`;
  }
  if (s.pickReco === "OSR automation") {
    return s.band === "A"
      ? `A-band mover, only ≈${s.faces} face-equivalents deep — automation pick rather than a lane of its own`
      : `${s.band}-band steady mover — automation carries it without holding a lane`;
  }
  return `${s.band}-band — too slow to hold automation capacity`;
}

function reportRows(reportId: ReportId): ReportRow[] {
  if (reportId === "storage") {
    return SKUS.filter((s) => s.pickReco !== "Small-bin")
      .sort((a, b) => b.vel - a.vel || a.id - b.id)
      .map((s) => ({
        skuId: s.id,
        cells: [
          s.name,
          s.vel.toLocaleString() + "/mo",
          s.inv.toLocaleString() + " boxes",
          "≈ " + s.faces.toLocaleString(),
          s.pickReco,
          recoWhy(s),
        ],
      }));
  }
  return SKUS.filter((s) => s.lastMovedQ >= 2).map((s) => ({
    skuId: s.id,
    cells: [
      s.name,
      s.lastMovedQ + "Q ago",
      s.type === "Dx" ? "£0 (Dx)" : fmtMoneyShort(s.value),
      s.type === "Dx" ? "Convert to Rx-packet" : "Relocate to hub / de-list",
      "No shipment in " + s.lastMovedQ + " quarters — a candidate to free its pick face",
    ],
  }));
}

/* ============ agent ============ */

/**
 * The demo's own anchors. `fieldAnswer` / `capacityAnswer` live in `agent.ts` so the snapshot
 * provider can hand them the real served figures instead (requirement R-012).
 */
const ANCHORS: AgentAnchors = {
  skus: SKUS,
  site: SITE,
  capacity: CAPACITY,
  levers: LEVERS,
  families: FAM,
  channels: CHANNELS,
  reloStops: RELO_STOPS,
};

/* ============ provider ============ */

export const demoDataProvider: DataProvider = {
  getSkus: () => Promise.resolve(SKUS),
  getSite: () => Promise.resolve(SITE),
  getCapacity: () => Promise.resolve(CAPACITY),
  getLevers: () => Promise.resolve([...LEVERS]),
  getLeverFamilies: (leverId: LeverId) => Promise.resolve(leverId === "cap" ? [] : FAM[leverId]),
  getReportRows: (reportId: ReportId) => Promise.resolve(reportRows(reportId)),
  askAgent: (question: string, context: AgentContext) =>
    Promise.resolve(
      context.surface === "capacity"
        ? capacityAnswer(question, context, ANCHORS)
        : fieldAnswer(question, context, ANCHORS),
    ),
};
